import { type ComponentProps, type ReactNode, Suspense, useMemo, useState, useTransition } from 'react';
import type { CheckboxProps } from 'react-aria-components';
import { Checkbox, CheckboxGroup, Collection, Text } from 'react-aria-components';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import {
  actionsRelatedToCollaboration,
  actionsRelatedToEnvironmentSettings,
  actionsRelatedToFlagSettings,
  actionsRelatedToTargetingChanges,
  allFlagActions,
} from '@gonfalon/audit-log/types';
import {
  enableAgentOptimization,
  enableAIBundle,
  enableAttributeDataSourceCreation,
  enableDomainVerificationForSSOAccounts,
  enableEnvironmentReleasePlans,
  enableIpAllowlist,
  enableReleaseAutomation,
  enableViews,
  viewAuditlogQueryingSupport,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { MemberAvatar } from '@gonfalon/members/MemberAvatar';
import { memberDisplayName } from '@gonfalon/members/memberFirstName';
import { memberInitials } from '@gonfalon/members/memberInitials';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { QueryProvider as ObservabilityQueryProvider } from '@gonfalon/observability-graphql/components/QueryProvider/QueryProvider';
import { useGetAlertOptionsQuery, useGetDashboardOptionsQuery } from '@gonfalon/observability-graphql/hooks';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import { useMember } from '@gonfalon/rest-api/hooks/useMember';
import { useTeam } from '@gonfalon/rest-api/hooks/useTeam';
import { getActionsQuery } from '@gonfalon/rest-api/queries/actions';
import { getAiConfigsQuery, listAgentOptimizationsQuery } from '@gonfalon/rest-api/queries/aiConfigs';
import { getAssignmentDataSourcesQuery } from '@gonfalon/rest-api/queries/assignmentDataSource';
import { getAttributeDataSourcesQuery } from '@gonfalon/rest-api/queries/attributeDataSource';
import { getEnvironmentQuery, getEnvironmentsByProjectQuery } from '@gonfalon/rest-api/queries/environments';
import { getFeatureFlagsQuery } from '@gonfalon/rest-api/queries/flags';
import { holdoutsList } from '@gonfalon/rest-api/queries/holdouts';
import { getMembersQuery } from '@gonfalon/rest-api/queries/members';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { getMetricGroupQuery, getMetricGroupsQuery } from '@gonfalon/rest-api/queries/metricGroups';
import { metricsDetail, metricsList } from '@gonfalon/rest-api/queries/metrics';
import { multiArmedBanditsList } from '@gonfalon/rest-api/queries/multiArmedBandits';
import { projectsList } from '@gonfalon/rest-api/queries/projects';
import { projectsDetail } from '@gonfalon/rest-api/queries/projectsDetail';
import { segmentList } from '@gonfalon/rest-api/queries/segments';
import { teamsList } from '@gonfalon/rest-api/queries/teams';
import { getViewQuery, getViewsQuery } from '@gonfalon/rest-api/queries/views';
import type { ActionTypes } from '@gonfalon/rest-api/types';
import { conditionalSuspenseQuery } from '@gonfalon/suspense/conditionalSuspenseQuery';
import { isSuspenseQueryEnabled } from '@gonfalon/suspense/isSuspenseQueryEnabled';
import { ListBoxItem } from '@gonfalon/ui/ListBox';
import type { ListBoxDialogProps } from '@gonfalon/ui/ListBoxDialog';
import { ListBoxDialog } from '@gonfalon/ui/ListBoxDialog';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Button,
  DialogTrigger,
  Header,
  ListBox,
  ListBoxSection,
  Checkbox as LPCheckBox,
  Popover,
  ProgressBar,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { isEqual } from 'lodash';
import nullthrows from 'nullthrows';

import { trackResourceFilterUpdated } from '../analytics';
import { anyAction, anyResource, resolveProjectKey } from '../ChangeHistory/ChangeHistoryUtils';
import { HelpDialog } from '../internal/HelpDialog';
import type {
  ActionComboBoxGroupItem,
  ActionComboBoxItem,
  DataSourceComboBoxItem,
  MemberComboBoxItem,
} from '../internal/ResourceFilters/types';
import { checkSubset, getCheckboxGroupValue } from '../internal/ResourceFilters/utils';
import { useChangeHistoryDisplayPreferences } from '../useChangeHistoryDisplayPreferences/useChangeHistoryDisplayPreferences';

import styles from './ActivityResourceFilters.module.css';

// REL-13059: the inline View filter only makes sense when the project actually has Views. Mirror the
// list-page views filter (BaseViewsFilter), which hides itself when the project has none, so the
// Change History View selector disappears too once the last View on a project is deleted.
function useProjectHasViews(projectKey: string): boolean {
  const enabled = enableViews() && viewAuditlogQueryingSupport() && Boolean(projectKey) && projectKey !== anyResource;
  const { data } = useQuery({
    ...getViewsQuery({
      projectKey,
      query: { limit: 1 },
    }),
    enabled,
  });
  return (data?.totalCount ?? 0) > 0;
}

export function FlagResourceFilter({
  onChange,
  project,
  flag,
  environments,
  actions,
  views,
}: {
  onChange: (nextFilter: {
    project: string;
    flag: string;
    environments: string[];
    actions: string[];
    views: string[];
  }) => void;
  project: string;
  flag: string;
  environments: string[];
  actions: string[];
  views: string[];
}) {
  const { updateWithSelection } = useChangeHistoryDisplayPreferences();

  // The inline View filter (REL-13059) is single-select; an empty array means "All". The chosen
  // View serializes onto the resource as `;view:<key>` so the server restricts the listing to the
  // View's current members of this kind.
  const selectedView = views[0] ?? anyResource;
  const hasViews = useProjectHasViews(project);

  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('flag', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    environments,
                    flag,
                    actions,
                    // The View belongs to the previous project, so reset it when the project changes.
                    views: [],
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      {enableViews() && viewAuditlogQueryingSupport() && hasViews && (
        <Box minWidth="0" flexShrink="0">
          <DialogTrigger>
            <Box marginRight="$300">
              <Button variant="default" className={styles.button} data-test-id="change-history-filter-view">
                <SelectedItem resource={msg('changeHistory.resourceFilter.view')} value={selectedView} />
                <Icon name="chevron-down" size="small" slot="end-icon" />
              </Button>
            </Box>
            <Popover>
              <ProjectScopedEntity project={project}>
                <ViewDialog
                  title={msg('changeHistory.resourcePicker.chooseView')}
                  viewKey={selectedView}
                  projectKey={project}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('flag', 'view', selection);
                    const [nextView] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                    onChange({
                      project,
                      flag,
                      environments,
                      actions,
                      views: String(nextView) === anyResource ? [] : [String(nextView)],
                    });
                  }}
                />
              </ProjectScopedEntity>
            </Popover>
          </DialogTrigger>
        </Box>
      )}

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-flag">
              {flag ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.flag')} value={flag} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.flag')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <FlagDialog
                title={msg('changeHistory.resourcePicker.chooseFlag')}
                flagKey={flag}
                projectKey={project}
                viewKey={views[0]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('flag', 'flag', selection);
                  const [selectedFlag] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({ environments, actions, project, views, flag: String(selectedFlag) });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.env')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                selectionMode="multiple"
                disallowEmptySelection
                defaultSelectedKeys={environments}
                environmentKeys={environments}
                projectKey={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('flag', 'environment', selection);
                  // Todo: update to typeguard
                  const environment = Array.from(selection) as string[];
                  const latestSelectedEnvironment = environment[environment.length - 1];
                  onChange({
                    project,
                    flag,
                    actions,
                    views,
                    environments:
                      latestSelectedEnvironment === anyResource
                        ? [String(anyResource)]
                        : environment.filter((env) => env !== anyResource),
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="flag"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          if (isEqual(action.sort(), allFlagActions.sort())) {
            updateWithSelection(new Set(['showAllActions']));
          } else {
            updateWithSelection(new Set([]));
          }
          onChange({
            project,
            flag,
            environments,
            views,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function EnvironmentResourceFilter({
  onChange,
  project,
  environments,
  actions,
}: {
  onChange: (nextFilter: { project: string; environments: string[]; actions: string[] }) => void;
  project: string;
  environments: string[];
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                disallowEmptySelection
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('env', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    environments: [anyResource],
                    actions,
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.environment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('env', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    environments: [String(selectedEnvironment)],
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="environment"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            environments,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function MemberResourceFilter({
  onChange,
  member,
  actions,
}: {
  onChange: (nextFilter: { member: string; actions: string[] }) => void;
  member: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-member">
              {member ? (
                <MemberSelectedValue resource={msg('changeHistory.resourceFilter.member')} value={member} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.member')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <MemberDialog
                title={msg('changeHistory.resourcePicker.chooseMember')}
                maintainerId={member}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('member', 'member', selection);
                  const [selectedMember] = Array.from(selection);
                  onChange({ actions, member: String(selectedMember) });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="member"
        onChange={(selectedActions) => {
          onChange({ member, actions: selectedActions });
        }}
      />
    </>
  );
}

// Global "who made the change" filter (the auditlog `member` query param). Unlike
// MemberResourceFilter above — which matches changes made TO a member account via a
// resource statement — this filters every entry by its actor.
export function ActivityMemberFilter({
  members,
  onChange,
}: {
  members: string[];
  onChange: (nextMembers: string[]) => void;
}) {
  const [selectedMember] = members;
  return (
    <Box minWidth="0" flexShrink="0">
      <DialogTrigger>
        {/* Fixed width once a member is selected: the label swaps from the raw
            member id to the resolved display name (and varies per member), and
            an auto-width button would re-flow the whole controls row. */}
        <Button variant="default" className={clsx(styles.button, selectedMember && styles.memberButton)}>
          {selectedMember ? (
            <MemberSelectedValue resource={msg('changeHistory.resourceFilter.member')} value={selectedMember} />
          ) : (
            <SelectedItem resource={msg('changeHistory.resourceFilter.member')} value="*" />
          )}
          <Icon name="chevron-down" size="small" slot="end-icon" />
        </Button>
        <Popover>
          <Suspense
            fallback={
              <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
              </Box>
            }
          >
            <MemberDialog
              title={msg('changeHistory.resourcePicker.filterByMember')}
              maintainerId={selectedMember}
              onSelectionChange={(selection) => {
                const [selected] = Array.from(selection);
                const nextMember = selected === undefined ? '*' : String(selected);
                onChange(nextMember === '*' ? [] : [nextMember]);
              }}
            />
          </Suspense>
        </Popover>
      </DialogTrigger>
    </Box>
  );
}

export function WildCardResourceFilter({ resource }: { resource: string }) {
  return (
    <Box minWidth="0" flexShrink="0">
      <Box>
        <Button variant="default" className={styles.button} isDisabled>
          <SelectedItem resource={resource} value="*" />
          <Icon name="chevron-down" size="small" slot="end-icon" />
        </Button>
      </Box>
    </Box>
  );
}

export function ProjectResourceFilter({
  onChange,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; actions: string[] }) => void;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('proj', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    actions,
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="member"
        onChange={(selectedActions) => {
          onChange({ project, actions: selectedActions });
        }}
      />
    </>
  );
}

export function MetricResourceFilter({
  onChange,
  metric,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; metric: string; actions: string[] }) => void;
  metric: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('metric', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project: String(selectedProject),
                    metric,
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-metric">
              {metric ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.metric')} value={metric} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.metric')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <MetricDialog
                title={msg('changeHistory.resourcePicker.chooseMetric')}
                metricKey={metric}
                project={project}
                className={styles.metricDialog}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('metric', 'metric', selection);
                  const [selectedMetric] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project,
                    metric: String(selectedMetric),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="metric"
        onChange={(selectedActions) => {
          onChange({ project, metric, actions: selectedActions });
        }}
      />
    </>
  );
}

export function MetricGroupResourceFilter({
  onChange,
  metricGroup,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; metricGroup: string; actions: string[] }) => void;
  metricGroup: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('metric-group', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project: String(selectedProject),
                    metricGroup,
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-metric-group">
              {metricGroup ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.metricGroup')} value={metricGroup} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.metricGroup')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <MetricGroupDialog
                title={msg('changeHistory.resourcePicker.chooseMetricGroup')}
                metricGroupKey={metricGroup}
                project={project}
                className={styles.metricDialog}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('metric-group', 'metricGroup', selection);
                  const [selectedMetricGroup] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project,
                    metricGroup: String(selectedMetricGroup),
                    actions,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="metric-group"
        onChange={(selectedActions) => {
          onChange({ project, metricGroup, actions: selectedActions });
        }}
      />
    </>
  );
}

type DataSourceFilterVariant = 'metric' | 'assignment' | 'attribute';

const DATA_SOURCE_RESOURCE_TYPE_BY_VARIANT = {
  metric: 'metric-data-source',
  assignment: 'assignment-data-source',
  attribute: 'attribute-data-source',
} as const satisfies Record<DataSourceFilterVariant, ActionTypes>;

export function DataSourceResourceFilter({
  onChange,
  project,
  environments,
  dataSource,
  actions,
  variant = 'metric',
}: {
  onChange: (nextFilter: { project: string; environments: string[]; dataSource: string; actions: string[] }) => void;
  project: string;
  environments: string[];
  dataSource: string;
  actions: string[];
  variant?: DataSourceFilterVariant;
}) {
  const projectContext = useProjectContext({ optional: true });
  const resourceType = DATA_SOURCE_RESOURCE_TYPE_BY_VARIANT[variant];
  const projectForQuery = resolveProjectKey(project, projectContext?.project?.key);
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resourceType, 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({
                    environments: [anyResource],
                    dataSource: anyResource,
                    actions,
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.environment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resourceType, 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    dataSource: anyResource,
                    actions,
                    environments: [String(selectedEnvironment)],
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-data-source">
              {dataSource ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.dataSource')} value={dataSource} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.dataSource')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={projectForQuery}>
              <DataSourceDialog
                title={msg('changeHistory.resourcePicker.chooseDataSource')}
                dataSourceKey={dataSource}
                project={projectForQuery}
                environment={environments[0]}
                variant={variant}
                className={styles.metricDialog}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resourceType, 'dataSource', selection);
                  const [selectedDataSource] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project,
                    environments,
                    dataSource: String(selectedDataSource),
                    actions,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource={resourceType}
        onChange={(selectedActions) => {
          onChange({ project, environments, dataSource, actions: selectedActions });
        }}
      />
    </>
  );
}

export function EnvironmentScopedResourceFilter({
  resource,
  onChange,
  project,
  environments,
  actions,
}: {
  resource: 'sdk-key' | 'environment-release-plan';
  onChange: (nextFilter: { project: string; environments: string[]; actions: string[] }) => void;
  project: string;
  environments: string[];
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                disallowEmptySelection
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resource, 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    environments: [anyResource],
                    actions,
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              <SelectedItem
                resource={msg('changeHistory.resourceFilter.env')}
                value={
                  environments.length === 1
                    ? environments[0]
                    : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                }
              />
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resource, 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    environments: [String(selectedEnvironment)],
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource={resource}
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            environments,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function ProjectScopedResourceFilter({
  onChange,
  project,
  resource,
}: {
  onChange: (nextFilter: { project: string }) => void;
  project: string;
  resource: string;
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated(resource, 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <WildCardResourceFilter resource={resource} />
    </>
  );
}

export function ExperimentsResourceFilter({
  onChange,
  project,
  environments,
  actions,
}: {
  onChange: (nextFilter: { project: string; environments: string[]; actions: string[] }) => void;
  project: string;
  environments: string[];
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('experiment', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    environments,
                    actions,
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.environment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('experiment', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    environments: [String(selectedEnvironment)],
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <WildCardResourceFilter resource="experiment" />
    </>
  );
}

export function MultiArmedBanditsResourceFilter({
  onChange,
  project,
  environments,
  multiArmedBandit,
  actions,
}: {
  onChange: (nextFilter: {
    project: string;
    environments: string[];
    actions: string[];
    multiArmedBanditKey: string;
  }) => void;
  project: string;
  environments: string[];
  multiArmedBandit: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('multi-armed-bandit', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    environments,
                    actions,
                    project: String(selectedProject),
                    multiArmedBanditKey: multiArmedBandit,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.environment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('multi-armed-bandit', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    environments: [String(selectedEnvironment)],
                    multiArmedBanditKey: multiArmedBandit,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-multi-armed-bandit">
              {multiArmedBandit ? (
                <SelectedItem resource="multi-armed-bandit" value={multiArmedBandit} />
              ) : (
                <DefaultItem resource="multi-armed-bandit" />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentScopedEntity environment={environments[0]}>
                <MultiArmedBanditDialog
                  title={msg('changeHistory.resourcePicker.chooseMultiArmedBandit')}
                  multiArmedBanditKey={multiArmedBandit}
                  projectKey={project}
                  environmentKey={environments[0]}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('multi-armed-bandit', 'multiArmedBandit', selection);
                    const [selectedMultiArmedBandit] = Array.from(selection).length
                      ? Array.from(selection)
                      : [anyResource];

                    onChange({
                      project,
                      environments,
                      actions,
                      multiArmedBanditKey: String(selectedMultiArmedBandit),
                    });
                  }}
                />
              </EnvironmentScopedEntity>
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="multi-armed-bandit"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            multiArmedBanditKey: multiArmedBandit,
            environments,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function HoldoutsResourceFilter({
  onChange,
  project,
  environments,
  holdout,
  actions,
}: {
  onChange: (nextFilter: { project: string; environments: string[]; actions: string[]; holdoutKey: string }) => void;
  project: string;
  environments: string[];
  holdout: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('holdout', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    environments,
                    actions,
                    project: String(selectedProject),
                    holdoutKey: holdout,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.environment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('holdout', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    environments: [String(selectedEnvironment)],
                    holdoutKey: holdout,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-holdout">
              {holdout ? <SelectedItem resource="holdout" value={holdout} /> : <DefaultItem resource="holdout" />}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentScopedEntity environment={environments[0]}>
                <HoldoutDialog
                  title={msg('changeHistory.resourcePicker.chooseHoldout')}
                  holdoutKey={holdout}
                  projectKey={project}
                  environmentKey={environments[0]}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('holdout', 'holdout', selection);
                    const [selectedHoldout] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                    onChange({
                      project,
                      environments,
                      actions,
                      holdoutKey: String(selectedHoldout),
                    });
                  }}
                />
              </EnvironmentScopedEntity>
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="holdout"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            holdoutKey: holdout,
            environments,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function TeamResourceFilter({
  onChange,
  team,
  actions,
}: {
  onChange: (nextFilter: { team: string; actions: string[] }) => void;
  team: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-team">
              {team ? (
                <TeamSelectedValue resource={msg('changeHistory.resourceFilter.team')} value={team} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.team')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <TeamDialog
                title={msg('changeHistory.resourcePicker.chooseTeam')}
                maintainerTeamKey={team}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('team', 'team', selection);
                  const [selectedTeam] = Array.from(selection);
                  onChange({ actions, team: String(selectedTeam) });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="team"
        onChange={(selectedActions) => {
          onChange({ team, actions: selectedActions });
        }}
      />
    </>
  );
}

export function SegmentResourceFilter({
  onChange,
  project,
  segment,
  environments,
  actions,
  views,
}: {
  onChange: (nextFilter: {
    project: string;
    segment: string;
    environments: string[];
    actions: string[];
    views: string[];
  }) => void;
  project: string;
  segment: string;
  environments: string[];
  actions: string[];
  views: string[];
}) {
  // Inline View filter (REL-13059): single-select; [] means "All".
  const selectedView = views[0] ?? anyResource;
  const hasViews = useProjectHasViews(project);

  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('segment', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    environments,
                    actions,
                    segment,
                    // The View belongs to the previous project, so reset it when the project changes.
                    views: [],
                    project: String(selectedProject),
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      {enableViews() && viewAuditlogQueryingSupport() && hasViews && (
        <Box minWidth="0" flexShrink="0">
          <DialogTrigger>
            <Box marginRight="$300">
              <Button variant="default" className={styles.button} data-test-id="change-history-filter-view">
                <SelectedItem resource={msg('changeHistory.resourceFilter.view')} value={selectedView} />
                <Icon name="chevron-down" size="small" slot="end-icon" />
              </Button>
            </Box>
            <Popover>
              <ProjectScopedEntity project={project}>
                <ViewDialog
                  title={msg('changeHistory.resourcePicker.chooseView')}
                  viewKey={selectedView}
                  projectKey={project}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('segment', 'view', selection);
                    const [nextView] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                    onChange({
                      project,
                      segment,
                      environments,
                      actions,
                      views: String(nextView) === anyResource ? [] : [String(nextView)],
                    });
                  }}
                />
              </ProjectScopedEntity>
            </Popover>
          </DialogTrigger>
        </Box>
      )}

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-segment">
              {segment ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.segment')} value={segment} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.segment')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentScopedEntity environment={environments[0]}>
                <SegmentDialog
                  title={msg('changeHistory.resourcePicker.chooseSegment')}
                  segmentKey={segment}
                  projectKey={project}
                  environmentKey={environments[0]}
                  viewKey={views[0]}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('segment', 'segment', selection);
                    const [selectedSegment] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                    onChange({
                      project,
                      environments,
                      actions,
                      views,
                      segment: String(selectedSegment),
                    });
                  }}
                />
              </EnvironmentScopedEntity>
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.env')} value={environments[0]} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.env')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('segment', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({
                    project,
                    segment,
                    actions,
                    views,
                    environments: [String(selectedEnvironment)],
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="segment"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            segment,
            environments,
            views,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function AIConfigResourceFilter({
  onChange,
  config,
  project,
  environments,
  actions,
  views,
}: {
  onChange: (nextFilter: {
    project: string;
    config: string;
    environments: string[];
    actions: string[];
    views: string[];
  }) => void;
  config: string;
  project: string;
  environments: string[];
  actions: string[];
  views: string[];
}) {
  const { updateWithSelection } = useChangeHistoryDisplayPreferences();

  // Inline View filter (REL-13059): single-select; [] means "All".
  const selectedView = views[0] ?? anyResource;
  const hasViews = useProjectHasViews(project);

  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('aiconfig', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({
                    environments,
                    config,
                    // The View belongs to the previous project, so reset it when the project changes.
                    views: [],
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      {enableViews() && viewAuditlogQueryingSupport() && hasViews && (
        <Box minWidth="0" flexShrink="0">
          <DialogTrigger>
            <Box marginRight="$300">
              <Button variant="default" className={styles.button} data-test-id="change-history-filter-view">
                <SelectedItem resource={msg('changeHistory.resourceFilter.view')} value={selectedView} />
                <Icon name="chevron-down" size="small" slot="end-icon" />
              </Button>
            </Box>
            <Popover>
              <ProjectScopedEntity project={project}>
                <ViewDialog
                  title={msg('changeHistory.resourcePicker.chooseView')}
                  viewKey={selectedView}
                  projectKey={project}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('aiconfig', 'view', selection);
                    const [nextView] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                    onChange({
                      project,
                      config,
                      environments,
                      actions,
                      views: String(nextView) === anyResource ? [] : [String(nextView)],
                    });
                  }}
                />
              </ProjectScopedEntity>
            </Popover>
          </DialogTrigger>
        </Box>
      )}
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-config">
              {config ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.config')} value={config} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.config')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <AIConfigDialog
                title={msg('changeHistory.resourcePicker.chooseConfig')}
                aiConfigKey={config}
                project={project}
                viewKey={views[0]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('aiconfig', 'config', selection);
                  const [selectedConfig] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({
                    project,
                    environments,
                    views,
                    config: String(selectedConfig),
                    actions,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-env">
              {environments ? (
                <SelectedItem
                  resource={msg('changeHistory.resourceFilter.env')}
                  value={
                    environments.length === 1
                      ? environments[0]
                      : msg('changeHistory.resourceFilter.environmentCount', { count: environments.length })
                  }
                />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.env')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <EnvironmentDialog
                disallowEmptySelection
                title={msg('changeHistory.resourcePicker.chooseEnvironment')}
                projectKey={project}
                selectionMode="single"
                defaultSelectedKeys={[environments[0]]}
                environmentKeys={[environments[0]]}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('aiconfig', 'environment', selection);
                  const [selectedEnvironment] = Array.from(selection);
                  onChange({
                    project,
                    actions,
                    views,
                    environments: [String(selectedEnvironment)],
                    config,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="aiconfig"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];
          // No allAiconfigActions, so just updateWithSelection for all
          if (action.includes(anyResource)) {
            updateWithSelection(new Set(['showAllActions']));
          } else {
            updateWithSelection(new Set([]));
          }
          onChange({
            project,
            config,
            environments,
            views,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </>
  );
}

export function AIModelConfigResourceFilter({
  onChange,
  config,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; config: string; actions: string[] }) => void;
  config: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('ai-model-config', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    config,
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="ai-model-config"
        onChange={(selectedActions) => {
          onChange({ project, config, actions: selectedActions });
        }}
      />
    </>
  );
}

export function AgentOptimizationResourceFilter({
  onChange,
  optimization,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; optimization: string; actions: string[] }) => void;
  optimization: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('agent-optimization', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    optimization,
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-optimization">
              {optimization ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.optimization')} value={optimization} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.optimization')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <AgentOptimizationDialog
                title={msg('changeHistory.resourcePicker.chooseOptimization')}
                optimizationKey={optimization}
                project={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('agent-optimization', 'optimization', selection);
                  const [selectedOptimization] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                  onChange({
                    project,
                    optimization: String(selectedOptimization),
                    actions,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="agent-optimization"
        onChange={(selectedActions) => {
          onChange({ project, optimization, actions: selectedActions });
        }}
      />
    </>
  );
}

export function AIDatasetResourceFilter({
  onChange,
  dataset,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; dataset: string; actions: string[] }) => void;
  dataset: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('ai-dataset', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    dataset,
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="ai-dataset"
        onChange={(selectedActions) => {
          onChange({ project, dataset, actions: selectedActions });
        }}
      />
    </>
  );
}

export function ViewResourceFilter({
  onChange,
  view,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; view: string; actions: string[] }) => void;
  view: string;
  project: string;
  actions: string[];
}) {
  return (
    <>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('view', 'project', selection);
                  const [selectedProject] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    view,
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-view">
              {view ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.view')} value={view} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.view')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <ProjectScopedEntity project={project}>
              <ViewDialog
                title={msg('changeHistory.resourcePicker.chooseView')}
                viewKey={view}
                projectKey={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('view', 'view', selection);
                  const [selectedView] = Array.from(selection).length ? Array.from(selection) : [anyResource];

                  onChange({
                    project,
                    view: String(selectedView),
                    actions,
                  });
                }}
              />
            </ProjectScopedEntity>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="view"
        onChange={(selectedActions) => {
          onChange({ project, view, actions: selectedActions });
        }}
      />
    </>
  );
}

type ViewComboBoxItem = {
  id: string;
  name: string;
};

type ViewDialogProps = Omit<ListBoxDialogProps<ViewComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  viewKey?: string;
  projectKey?: string;
};

function ViewDialog({ viewKey, projectKey, ...props }: ViewDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const { data: views } = useSuspenseQuery({
    ...getViewsQuery({
      projectKey: String(projectKey),
      query: { limit: 10, ...(filterText ? { filter: { query: filterText } } : {}) },
    }),
  });

  // Ensure the currently selected view is present even when it falls outside the
  // limited page returned above (mirrors MetricDialog's metricsDetail prepend).
  const [selectedViewQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: getViewQuery({
          projectKey: String(projectKey),
          viewKey: viewKey ?? '',
        }),
        enabled: viewKey !== undefined && viewKey !== anyResource,
      }),
    ],
  });

  const viewItems: ViewComboBoxItem[] = views.items
    .filter((view) => view.key !== viewKey)
    .map((view) => ({
      id: view.key,
      name: view.name,
    }));

  if (isSuspenseQueryEnabled(selectedViewQuery) && selectedViewQuery.data) {
    viewItems.unshift({
      id: selectedViewQuery.data.key,
      name: selectedViewQuery.data.name,
    });
  }

  viewItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<ViewComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.viewsLabel')}
      items={viewItems}
      selectionMode="single"
      defaultSelectedKeys={viewKey !== undefined ? [viewKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

export function AlertResourceFilter({
  onChange,
  project,
  alert,
  actions,
}: {
  onChange: (nextFilter: { project: string; alert: string; actions: string[] }) => void;
  project: string;
  alert: string;
  actions: string[];
}) {
  const [selectedProjectQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: projectsDetail({ projectKey: project ?? '' }),
        enabled: project !== undefined && project !== anyResource,
      }),
    ],
  });

  const projectId = useMemo(() => {
    if (isSuspenseQueryEnabled(selectedProjectQuery) && selectedProjectQuery.data) {
      return selectedProjectQuery.data._id;
    }
    return undefined;
  }, [selectedProjectQuery]);

  return (
    <ObservabilityQueryProvider>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('alert', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    project: String(selectedProject),
                    alert,
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-alert">
              {alert ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.alert')} value={alert} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.alert')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectScopedEntity project={project}>
                <AlertDialog
                  title={msg('changeHistory.resourcePicker.chooseAlert')}
                  projectKey={project}
                  projectId={projectId}
                  alertKey={alert}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('alert', 'alert', selection);
                    const [selectedAlert] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                    onChange({
                      project,
                      alert: String(selectedAlert),
                      actions,
                    });
                  }}
                />
              </ProjectScopedEntity>
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="alert"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];

          onChange({
            project,
            alert,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </ObservabilityQueryProvider>
  );
}

export function ObservabilitySettingsResourceFilter({
  onChange,
  project,
  actions,
}: {
  onChange: (nextFilter: { project: string; actions: string[] }) => void;
  project: string;
  actions: string[];
}) {
  return (
    <ObservabilityQueryProvider>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('observability-settings', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    project: String(selectedProject),
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>
      <ActionsResourceFilter
        actions={actions}
        resource="observability-settings"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];
          onChange({
            project,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </ObservabilityQueryProvider>
  );
}

export function ObservabilityDashboardResourceFilter({
  onChange,
  project,
  dashboard,
  actions,
}: {
  onChange: (nextFilter: { project: string; dashboard: string; actions: string[] }) => void;
  project: string;
  dashboard: string;
  actions: string[];
}) {
  const [selectedProjectQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: projectsDetail({ projectKey: project ?? '' }),
        enabled: project !== undefined && project !== anyResource,
      }),
    ],
  });

  const projectId = useMemo(() => {
    if (isSuspenseQueryEnabled(selectedProjectQuery) && selectedProjectQuery.data) {
      return selectedProjectQuery.data._id;
    }
    return undefined;
  }, [selectedProjectQuery]);

  return (
    <ObservabilityQueryProvider>
      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-project">
              {project ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.project')} value={project} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.project')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectDialog
                title={msg('changeHistory.resourcePicker.chooseProject')}
                projectId={project}
                onSelectionChange={(selection) => {
                  trackResourceFilterUpdated('observability-dashboard', 'project', selection);
                  const [selectedProject] = Array.from(selection);
                  onChange({
                    project: String(selectedProject),
                    dashboard,
                    actions,
                  });
                }}
              />
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <Box minWidth="0" flexShrink="0">
        <DialogTrigger>
          <Box marginRight="$300">
            <Button variant="default" className={styles.button} data-test-id="change-history-filter-dashboard">
              {dashboard ? (
                <SelectedItem resource={msg('changeHistory.resourceFilter.dashboard')} value={dashboard} />
              ) : (
                <DefaultItem resource={msg('changeHistory.resourceFilter.dashboard')} />
              )}
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
          </Box>
          <Popover>
            <Suspense
              fallback={
                <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
                </Box>
              }
            >
              <ProjectScopedEntity project={project}>
                <ObservabilityDashboardDialog
                  title={msg('changeHistory.resourcePicker.chooseDashboard')}
                  dashboardKey={dashboard}
                  projectId={projectId}
                  onSelectionChange={(selection) => {
                    trackResourceFilterUpdated('observability-dashboard', 'dashboard', selection);
                    const [selectedDashboard] = Array.from(selection).length ? Array.from(selection) : [anyResource];
                    onChange({
                      project,
                      dashboard: String(selectedDashboard),
                      actions,
                    });
                  }}
                />
              </ProjectScopedEntity>
            </Suspense>
          </Popover>
        </DialogTrigger>
      </Box>

      <ActionsResourceFilter
        actions={actions}
        resource="observability-dashboard"
        onChange={(selection) => {
          const action = Array.from(selection);
          const latestSelectedAction = action[action.length - 1];
          onChange({
            project,
            dashboard,
            actions: latestSelectedAction === anyResource ? anyAction : action.filter((a) => a !== anyResource),
          });
        }}
      />
    </ObservabilityQueryProvider>
  );
}

export function ActionsResourceFilter({
  onChange,
  actions,
  resource,
}: {
  actions: string[];
  resource: ActionTypes;
  onChange: (actions: string[]) => void;
}) {
  return (
    <Box minWidth="0" flexShrink="0">
      <DialogTrigger>
        <Box marginRight="$200">
          <Button variant="default" className={styles.button} data-test-id="change-history-filter-actions">
            {actions ? (
              <SelectedItem
                resource={msg('changeHistory.resourceFilter.actions')}
                value={
                  actions.length === 1
                    ? actions[0]
                    : msg('changeHistory.resourceFilter.actionCount', { count: actions.length })
                }
              />
            ) : (
              <DefaultItem resource={msg('changeHistory.resourceFilter.actions')} />
            )}

            <Icon name="chevron-down" size="small" slot="end-icon" />
          </Button>
        </Box>
        <Popover>
          <Suspense
            fallback={
              <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
                <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
              </Box>
            }
          >
            <ActionsDialog
              title={msg('changeHistory.resourcePicker.chooseAction')}
              actions={actions}
              resource={resource}
              onSelectionChange={(selection) => {
                trackResourceFilterUpdated(resource, 'actions', selection);
                const selectedActions = Array.from(selection);
                onChange(selectedActions as string[]);
              }}
            />
          </Suspense>
        </Popover>
      </DialogTrigger>
    </Box>
  );
}

function ProjectScopedEntity({ project, children }: { project?: string; children: ReactNode }) {
  if (!project || project === '*') {
    return (
      <HelpDialog
        title={msg('changeHistory.resourcePicker.infoTitle')}
        message={msg('changeHistory.resourcePicker.pickProjectFirst')}
      />
    );
  }
  return (
    <Suspense
      fallback={
        <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
          <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
        </Box>
      }
    >
      {children}
    </Suspense>
  );
}
function EnvironmentScopedEntity({ environment, children }: { environment?: string; children: ReactNode }) {
  if (!environment || environment === '*') {
    return (
      <HelpDialog
        title={msg('changeHistory.resourcePicker.infoTitle')}
        message={msg('changeHistory.resourcePicker.pickEnvironmentFirst')}
      />
    );
  }
  return (
    <Suspense
      fallback={
        <Box width="$192" height="$208" display="grid" alignItems="center" justifyContent="center">
          <ProgressBar aria-label={msg('changeHistory.status.loading')} isIndeterminate size="small" />
        </Box>
      }
    >
      {children}
    </Suspense>
  );
}
function SelectedItem({ resource, value }: { resource: string; value: string }) {
  const coercedAnyValue = value === '*' ? msg('changeHistory.resourceFilter.all') : value;
  return (
    <TextTruncator>
      <p className={styles.selectedItemPrefix}>{resource}:</p> {coercedAnyValue}
    </TextTruncator>
  );
}

function MemberSelectedValue({ resource, value }: { resource: string; value: string }) {
  const isAllMembers = value === '*';
  let memberName;
  const maintainerMember = useMember({ memberId: value }, { enabled: !isAllMembers });

  if (maintainerMember.status === 'success') {
    const member = maintainerMember.data;
    const name = memberDisplayName(member);
    memberName = name;
  }

  return <SelectedItem resource={resource} value={memberName ?? value} />;
}

function TeamSelectedValue({ resource, value }: { resource: string; value: string }) {
  const isAllTeams = value === '*';
  let teamName;
  const maintainerTeam = useTeam({ teamKey: value }, { enabled: !isAllTeams });

  if (maintainerTeam.status === 'success') {
    const team = maintainerTeam.data;
    teamName = team.name;
  }

  return <SelectedItem resource={resource} value={teamName ?? value} />;
}

function DefaultItem({ resource }: { resource: string }) {
  return (
    <TextTruncator>
      {resource} <Icon name="chevrons-out" size="small" />
    </TextTruncator>
  );
}

export const ActivityResourceFiltersOptions = [
  {
    name: 'Access token',
    value: 'token',
  },
  {
    name: 'Account',
    value: 'acct',
  },
  ...(enableAgentOptimization()
    ? [
        {
          name: 'Agent optimization',
          value: 'agent-optimization',
        },
      ]
    : []),
  ...(enableAIBundle()
    ? [
        {
          name: 'AI config',
          value: 'aiconfig',
        },
      ]
    : []),
  ...(enableAIBundle()
    ? [
        {
          name: 'AI Model Config',
          value: 'ai-model-config',
        },
      ]
    : []),
  {
    name: 'Alert',
    value: 'alert',
  },
  {
    name: 'Application',
    value: 'application',
  },
  {
    name: 'Assignment data source',
    value: 'assignment-data-source',
  },
  ...(enableAttributeDataSourceCreation()
    ? [
        {
          name: 'Attribute data source',
          value: 'attribute-data-source',
        },
      ]
    : []),
  {
    name: 'Code reference',
    value: 'code-reference-repository',
  },
  {
    name: 'Context kinds',
    value: 'context-kind',
  },
  ...(enableAIBundle()
    ? [
        {
          name: 'Dataset',
          value: 'ai-dataset',
        },
      ]
    : []),
  ...(enableDomainVerificationForSSOAccounts()
    ? [
        {
          name: 'Domain verification',
          value: 'domain-verification',
        },
      ]
    : []),
  {
    name: 'Environment',
    value: 'env',
  },
  ...(enableEnvironmentReleasePlans()
    ? [
        {
          name: 'Environment release plan',
          value: 'environment-release-plan',
        },
      ]
    : []),
  {
    name: 'Experiment',
    value: 'experiment',
  },
  {
    name: 'Flag',
    value: 'flag',
  },
  {
    name: 'Holdout',
    value: 'holdout',
  },
  {
    name: 'Integration',
    value: 'integration',
  },
  ...(enableIpAllowlist()
    ? [
        {
          name: 'IP allowlist',
          value: 'ip-allowlist',
        },
      ]
    : []),
  {
    name: 'Layer',
    value: 'layer',
  },
  {
    name: 'Member',
    value: 'member',
  },
  {
    name: 'Metric',
    value: 'metric',
  },
  {
    name: 'Metric group',
    value: 'metric-group',
  },
  {
    name: 'Metric data source',
    value: 'metric-data-source',
  },
  {
    name: 'Multi-armed bandit',
    value: 'multi-armed-bandit',
  },
  {
    name: 'Observability dashboard',
    value: 'observability-dashboard',
  },
  {
    name: 'Observability settings',
    value: 'observability-settings',
  },
  {
    name: 'Payload filter',
    value: 'payload-filter',
  },
  {
    name: 'Project',
    value: 'proj',
  },
  {
    name: 'Relay Proxy',
    value: 'relay-proxy-config',
  },
  ...(enableReleaseAutomation()
    ? [
        {
          name: 'Release pipeline',
          value: 'release-pipeline',
        },
      ]
    : []),
  {
    name: 'Release policy',
    value: 'release-policy',
  },
  {
    name: 'Role',
    value: 'role',
  },
  {
    name: 'SDK key',
    value: 'sdk-key',
  },
  {
    name: 'Segment',
    value: 'segment',
  },
  {
    name: 'Service token',
    value: 'service-token',
  },
  {
    name: 'Team',
    value: 'team',
  },
  {
    name: 'Template',
    value: 'template',
  },
  ...(enableViews()
    ? [
        {
          name: 'View',
          value: 'view',
        },
      ]
    : []),
  {
    name: 'Webhook',
    value: 'webhook',
  },
];

export function ResourceKindPicker({
  value,
  onSelectionChange,
}: {
  value: ResourceSpecifier['type'];
  onSelectionChange: ComponentProps<typeof Select>['onSelectionChange'];
}) {
  return (
    <Select
      aria-label={msg('changeHistory.resourceFilter.kindPrefix')}
      selectedKey={value}
      defaultSelectedKey={value}
      onSelectionChange={onSelectionChange}
    >
      <Button variant="default" size="medium" className={styles.button}>
        <TextTruncator>
          <span className={styles.selectedItemPrefix}>
            <Message id="changeHistory.resourceFilter.kindPrefix" />
          </span>{' '}
          <SelectValue />
        </TextTruncator>
        <Icon name="chevron-down" size="small" slot="end-icon" />
      </Button>
      <Popover>
        <ListBox className={styles.resourceKindPicker}>
          {ActivityResourceFiltersOptions.map((item) => (
            <ListBoxItem key={item.value} id={item.value} textValue={item.value} className={styles.listBoxItem}>
              <Text slot="label" className={styles.selectValueTruncate}>
                <TextTruncator>{item.name}</TextTruncator>
              </Text>
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}

type SegmentComboBoxItem = {
  id: string;
  name: string;
  description?: string;
};
type SegmentDialogProps = Omit<ListBoxDialogProps<SegmentComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  segmentKey?: string;
  projectKey?: string;
  environmentKey?: string;
  // REL-13059: when a View is selected on this row, reduce the picker to the View's segment members.
  viewKey?: string;
};

function SegmentDialog({ segmentKey, projectKey, environmentKey, viewKey, ...props }: SegmentDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [
      segmentList({
        projectKey: String(projectKey),
        environmentKey: String(environmentKey),
        params: {
          expand: 'flags',
          limit: 10,
          sort: '-lastModified',
          filter: { query: filterText, ...(viewKey ? { view: viewKey } : {}) },
        },
      }),
    ],
  });
  const [segments] = data;

  const segmentItems: SegmentComboBoxItem[] = segments.data.items.map((segment) => ({
    id: segment.key,
    name: segment.name,
  }));

  segmentItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<SegmentComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.segmentsLabel')}
      items={segmentItems.length ? segmentItems : []}
      selectionMode="single"
      defaultSelectedKeys={segmentKey !== undefined ? [segmentKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type AIConfigComboBoxItem = {
  id: string;
  name: string;
};

type AIConfigDialogProps = Omit<ListBoxDialogProps<AIConfigComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  project: string;
  aiConfigKey?: string;
  // REL-13059: when a View is selected on this row, reduce the picker to the View's AI config members.
  viewKey?: string;
};

function AIConfigDialog({ aiConfigKey, project, viewKey, ...props }: AIConfigDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const { data: aiConfigs } = useSuspenseQuery({
    ...getAiConfigsQuery({
      projectKey: project,
      query: {
        filter: {
          query: filterText || undefined,
          ...(viewKey ? { view: viewKey } : {}),
        },
        limit: 50, // Reasonable limit for the dialog
        sort: 'name', // Sort by name for better UX
      },
    }),
  });

  const aiConfigItems: AIConfigComboBoxItem[] = aiConfigs.items.map((config) => ({
    id: config.key,
    name: config.name,
  }));

  aiConfigItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<AIConfigComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.configsLabel')}
      items={aiConfigItems.length ? aiConfigItems : []}
      selectionMode="single"
      defaultSelectedKeys={aiConfigKey !== undefined ? [aiConfigKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type AgentOptimizationComboBoxItem = {
  id: string;
  name: string;
};

type AgentOptimizationDialogProps = Omit<
  ListBoxDialogProps<AgentOptimizationComboBoxItem>,
  'isBusy' | 'onFilterChange' | 'children'
> & {
  project: string;
  optimizationKey?: string;
};

function AgentOptimizationDialog({ optimizationKey, project, ...props }: AgentOptimizationDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const { data: optimizations } = useSuspenseQuery({
    ...listAgentOptimizationsQuery({
      projectKey: project,
      query: {
        limit: 50,
        offset: 0,
      },
    }),
  });

  const items: AgentOptimizationComboBoxItem[] = (optimizations.items ?? [])
    .filter((opt) => !filterText || opt.key?.toLowerCase().includes(filterText.toLowerCase()))
    .map((opt) => ({
      id: opt.key ?? '',
      name: opt.key ?? '',
    }));

  items.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<AgentOptimizationComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.optimizationsLabel')}
      items={items.length ? items : []}
      selectionMode="single"
      defaultSelectedKeys={optimizationKey !== undefined ? [optimizationKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type MultiArmedBanditComboBoxItem = {
  id: string;
  name: string;
};

type MultiArmedBanditDialogProps = Omit<
  ListBoxDialogProps<MultiArmedBanditComboBoxItem>,
  'isBusy' | 'onFilterChange' | 'children'
> & {
  multiArmedBanditKey?: string;
  projectKey?: string;
  environmentKey?: string;
};
function MultiArmedBanditDialog({
  multiArmedBanditKey,
  projectKey,
  environmentKey,
  ...props
}: MultiArmedBanditDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };
  const data = useSuspenseQueries({
    queries: [
      multiArmedBanditsList({
        projectKey: String(projectKey),
        environmentKey: String(environmentKey),
      }),
    ],
  });
  const [multiArmedBandits] = data;

  let multiArmedBanditItems: MultiArmedBanditComboBoxItem[] = (multiArmedBandits?.data?.items || []).map((mab) => ({
    id: mab.key || '',
    name: mab.name || '',
  }));

  multiArmedBanditItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  multiArmedBanditItems = multiArmedBanditItems.filter((item) =>
    item.name.toLowerCase().includes(filterText.toLowerCase()),
  );

  return (
    <ListBoxDialog<MultiArmedBanditComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.multiArmedBanditsLabel')}
      items={multiArmedBanditItems.length ? multiArmedBanditItems : []}
      selectionMode="single"
      defaultSelectedKeys={multiArmedBanditKey !== undefined ? [multiArmedBanditKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type HoldoutComboBoxItem = {
  id: string;
  name: string;
};

type HoldoutDialogProps = Omit<ListBoxDialogProps<HoldoutComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  holdoutKey?: string;
  projectKey?: string;
  environmentKey?: string;
};
function HoldoutDialog({ holdoutKey, projectKey, environmentKey, ...props }: HoldoutDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };
  const data = useSuspenseQueries({
    queries: [
      holdoutsList({
        projectKey: String(projectKey),
        environmentKey: String(environmentKey),
      }),
    ],
  });
  const [holdouts] = data;

  let holdoutItems: HoldoutComboBoxItem[] = (holdouts?.data?.items || []).map((h) => ({
    id: h.key || '',
    name: h.name || '',
  }));

  holdoutItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  holdoutItems = holdoutItems.filter((item) => item.name.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <ListBoxDialog<HoldoutComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.holdoutsLabel')}
      items={holdoutItems.length ? holdoutItems : []}
      selectionMode="single"
      defaultSelectedKeys={holdoutKey !== undefined ? [holdoutKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type EnvironmentComboBoxItem = {
  id: string;
  name: string;
  color: string;
  description?: string;
};
type EnvironmentDialogProps = Omit<
  ListBoxDialogProps<EnvironmentComboBoxItem>,
  'isBusy' | 'onFilterChange' | 'children'
> & {
  environmentKeys: string[];
  projectKey?: string;
};
export function EnvironmentDialog({ environmentKeys, projectKey, ...props }: EnvironmentDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [
      getEnvironmentsByProjectQuery({
        projectKey: String(projectKey),
        query: { limit: 10, sort: 'name', filter: { query: filterText } },
      }),
    ],
  });
  const [environments] = data;

  const specificEnvironmentKeys = environmentKeys.filter((key) => key !== anyResource);
  const selectedEnvironmentsData = useSuspenseQueries({
    queries: specificEnvironmentKeys.map((key) =>
      getEnvironmentQuery({
        projectKey: String(projectKey),
        environmentKey: key,
      }),
    ),
  });

  const environmentItems: EnvironmentComboBoxItem[] = selectedEnvironmentsData.map((env) => ({
    id: env.data.key,
    name: env.data.name,
    color: env.data.color,
  }));

  environments.data.items.forEach((env) => {
    if (!environmentItems.some((item) => item.id === env.key)) {
      environmentItems.push({
        id: env.key,
        name: env.name,
        color: env.color,
      });
    }
  });

  environmentItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
    color: '',
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<EnvironmentComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.environmentsLabel')}
      items={environmentItems.length ? environmentItems : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
      onSelectionChange={(selection) => {
        props?.onSelectionChange && props.onSelectionChange(selection);
      }}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            {item.color && <EnvironmentMarker color={item.color} />}
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type FlagComboBoxItem = {
  id: string;
  name: string;
  description?: string;
};
type FlagDialogProps = Omit<ListBoxDialogProps<FlagComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  flagKey?: string;
  projectKey?: string;
  // REL-13059: when a View is selected on this row, reduce the picker to the View's flag members.
  viewKey?: string;
};
function FlagDialog({ flagKey, projectKey, viewKey, ...props }: FlagDialogProps) {
  // must be optional since we are using this component in a non-context route (settings pages)
  const selectedEnvironmentKey = useSelectedEnvironmentKey({ optional: true });
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [
      getFeatureFlagsQuery({
        projectKey: String(projectKey),
        query: {
          limit: 10,
          sort: '-creationDate',
          env: selectedEnvironmentKey ? [selectedEnvironmentKey] : undefined,
          filter: { query: filterText, ...(viewKey ? { view: viewKey } : {}) },
        },
      }),
    ],
  });
  const [flags] = data;

  const flagItems: FlagComboBoxItem[] = flags.data.items.map((flag) => ({
    id: flag.key,
    name: flag.name,
  }));

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  flagItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  return (
    <ListBoxDialog<FlagComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.flagsLabel')}
      items={flagItems.length ? flagItems : []}
      selectionMode="single"
      defaultSelectedKeys={flagKey !== undefined ? [flagKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} textValue={item.id}>
          <Text slot="label">
            <Icon name="flag" size="small" />
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type ProjectComboBoxItem = {
  id: string;
  name: string;
  description?: string;
};
type ProjectDialogProps = Omit<ListBoxDialogProps<ProjectComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  projectId?: string;
};
function ProjectDialog({ projectId, ...props }: ProjectDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [projectsList({ params: { limit: 10, sort: 'name', filter: { query: filterText } } })],
  });
  const [projects] = data;

  const projectItems: ProjectComboBoxItem[] = projects.data.items.map((project) => ({
    id: project.key,
    name: project.name,
  }));

  const [selectedProjectQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: projectsDetail({ projectKey: projectId ?? '' }),
        enabled: projectId !== undefined && projectId !== anyResource,
      }),
    ],
  });

  if (isSuspenseQueryEnabled(selectedProjectQuery) && selectedProjectQuery.data) {
    projectItems.unshift({
      id: selectedProjectQuery.data.key,
      name: selectedProjectQuery.data.name,
    });
  }

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  projectItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  return (
    <ListBoxDialog<ProjectComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.projectsLabel')}
      items={projectItems.length ? projectItems : []}
      selectionMode="single"
      defaultSelectedKeys={projectId !== undefined ? [projectId] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type MetricComboBoxItem = {
  id: string;
  name: string;
  description?: string;
};
type MetricDialogProps = Omit<ListBoxDialogProps<MetricComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  project: string;
  metricKey?: string;
};

function MetricDialog({ metricKey, project, ...props }: MetricDialogProps) {
  const [isPending] = useTransition();
  const [metricQuery, setMetricQuery] = useState('');
  const data = useSuspenseQueries({
    queries: [metricsList({ projectKey: project, params: { sort: 'name' } })],
  });
  const [metrics] = data;

  const metricItems: MetricComboBoxItem[] =
    metrics?.data?.items
      ?.filter((metric) => metricQuery === '' || metric.name.toLowerCase().includes(metricQuery.toLowerCase()))
      .map((metric) => ({
        id: metric.key,
        name: metric.name,
      })) ?? [];

  const [selectedMetricQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: metricsDetail({ projectKey: project, metricKey: metricKey ?? '' }),
        enabled: metricKey !== undefined && metricKey !== anyResource,
      }),
    ],
  });

  if (isSuspenseQueryEnabled(selectedMetricQuery) && selectedMetricQuery.data) {
    metricItems.unshift({
      id: selectedMetricQuery.data.key,
      name: selectedMetricQuery.data.name,
    });
  }

  metricItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  return (
    <ListBoxDialog<MetricComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.metricsLabel')}
      items={metricItems.length ? metricItems : []}
      selectionMode="single"
      defaultSelectedKeys={metricKey !== undefined ? [metricKey] : []}
      isBusy={isPending}
      onFilterChange={setMetricQuery}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type MetricGroupDialogProps = Omit<ListBoxDialogProps<MetricComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  project: string;
  metricGroupKey?: string;
};

function MetricGroupDialog({ metricGroupKey, project, ...props }: MetricGroupDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [metricGroupQuery, setMetricGroupQuery] = useState('');
  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setMetricGroupQuery(value);
    });
  };
  const [metricGroups] = useSuspenseQueries({
    queries: [
      getMetricGroupsQuery({
        projectKey: project,
        query: { sort: 'name', limit: 50, filter: metricGroupQuery ? { query: metricGroupQuery } : undefined },
      }),
    ],
  });

  const metricGroupItems: MetricComboBoxItem[] =
    metricGroups?.data?.items?.map((group) => ({
      id: group.key,
      name: group.name,
    })) ?? [];

  const [selectedMetricGroupQuery] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: getMetricGroupQuery({ projectKey: project, metricGroupKey: metricGroupKey ?? '' }),
        enabled: metricGroupKey !== undefined && metricGroupKey !== anyResource,
      }),
    ],
  });

  if (
    isSuspenseQueryEnabled(selectedMetricGroupQuery) &&
    selectedMetricGroupQuery.data &&
    !metricGroupItems.some((item) => item.id === selectedMetricGroupQuery.data.key)
  ) {
    metricGroupItems.unshift({
      id: selectedMetricGroupQuery.data.key,
      name: selectedMetricGroupQuery.data.name,
    });
  }

  metricGroupItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  return (
    <ListBoxDialog<MetricComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.metricGroupsLabel')}
      items={metricGroupItems}
      selectionMode="single"
      defaultSelectedKeys={metricGroupKey !== undefined ? [metricGroupKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

export interface DataSourceDialogProps extends Omit<
  ListBoxDialogProps<DataSourceComboBoxItem>,
  'isBusy' | 'onFilterChange' | 'children'
> {
  project: string;
  environment: string;
  dataSourceKey?: string;
  variant?: DataSourceFilterVariant;
}

function DataSourceDialog({ variant = 'metric', ...props }: DataSourceDialogProps) {
  if (variant === 'assignment') {
    return <AssignmentDataSourceDialog {...props} />;
  }

  if (variant === 'attribute') {
    return <AttributeDataSourceDialog {...props} />;
  }

  return <MetricDataSourceDialog {...props} />;
}

function AttributeDataSourceDialog({
  dataSourceKey,
  project,
  environment,
  ...props
}: Omit<DataSourceDialogProps, 'variant'>) {
  const [isPending] = useTransition();
  const [dataSourceQuery, setDataSourceQuery] = useState('');
  const { data } = useSuspenseQuery(getAttributeDataSourcesQuery({ projKey: project }));

  return (
    <DataSourceListDialog
      {...props}
      dataSourceKey={dataSourceKey}
      environment={environment}
      isPending={isPending}
      dataSourceQuery={dataSourceQuery}
      onFilterChange={setDataSourceQuery}
      dataSources={data?.items}
    />
  );
}

function AssignmentDataSourceDialog({
  dataSourceKey,
  project,
  environment,
  ...props
}: Omit<DataSourceDialogProps, 'variant'>) {
  const [isPending] = useTransition();
  const [dataSourceQuery, setDataSourceQuery] = useState('');
  const { data } = useSuspenseQuery(getAssignmentDataSourcesQuery({ projKey: project }));

  return (
    <DataSourceListDialog
      {...props}
      dataSourceKey={dataSourceKey}
      environment={environment}
      isPending={isPending}
      dataSourceQuery={dataSourceQuery}
      onFilterChange={setDataSourceQuery}
      dataSources={data?.items}
    />
  );
}

function MetricDataSourceDialog({
  dataSourceKey,
  project,
  environment,
  ...props
}: Omit<DataSourceDialogProps, 'variant'>) {
  const [isPending] = useTransition();
  const [dataSourceQuery, setDataSourceQuery] = useState('');
  const { data } = useSuspenseQuery(getMetricDataSourcesQuery({ projKey: project }));

  return (
    <DataSourceListDialog
      {...props}
      dataSourceKey={dataSourceKey}
      environment={environment}
      isPending={isPending}
      dataSourceQuery={dataSourceQuery}
      onFilterChange={setDataSourceQuery}
      dataSources={data?.items}
    />
  );
}

function DataSourceListDialog({
  dataSourceKey,
  environment,
  dataSources,
  isPending,
  dataSourceQuery,
  onFilterChange,
  ...props
}: Omit<DataSourceDialogProps, 'variant' | 'project'> & {
  dataSources?: Array<{ key: string; name: string; environmentKey: string }>;
  isPending: boolean;
  dataSourceQuery: string;
  onFilterChange: (value: string) => void;
}) {
  const dataSourceItems: DataSourceComboBoxItem[] =
    dataSources
      ?.filter((dataSource) => {
        const matchesEnvironment = environment === anyResource || dataSource.environmentKey === environment;
        const matchesQuery =
          dataSourceQuery === '' || dataSource.name.toLowerCase().includes(dataSourceQuery.toLowerCase());
        return matchesEnvironment && matchesQuery;
      })
      .map((dataSource) => ({
        id: dataSource.key,
        name: dataSource.name,
      })) ?? [];

  dataSourceItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  return (
    <ListBoxDialog<DataSourceComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.dataSourcesLabel')}
      items={dataSourceItems.length ? dataSourceItems : []}
      selectionMode="single"
      defaultSelectedKeys={dataSourceKey !== undefined ? [dataSourceKey] : []}
      isBusy={isPending}
      onFilterChange={onFilterChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

function SelectAllCheckbox({ children, ...props }: CheckboxProps) {
  return <Checkbox {...props}>{children}</Checkbox>;
}

interface ActionDialogProps extends Omit<ListBoxDialogProps<ActionComboBoxGroupItem>, 'isBusy' | 'onFilterChange'> {
  actions?: string[];
  resource: ActionTypes;
}

function ActionsDialog({ actions, resource, ...props }: ActionDialogProps) {
  const [filterText, setFilterText] = useState('');
  const actionResult = useSuspenseQuery(getActionsQuery());
  const actionData = actionResult.data[resource] ?? [];

  const filterOutReleaseActions = (action: ActionComboBoxItem) => {
    if (enableReleaseAutomation()) {
      return true;
    }
    return ![
      'updateReleasePhaseStatus',
      'addReleasePipeline',
      'removeReleasePipeline',
      'replaceReleasePipeline',
      'updateReleasePhaseCompleted',
      'terminateReleaseAudience',
    ].includes(action.id);
  };

  const actionsItems: ActionComboBoxItem[] = actionData
    .map((action) => ({
      id: nullthrows(action.action),
      description: action.description,
    }))
    .toSorted((a, b) => a.id.localeCompare(b.id))
    .filter(filterOutReleaseActions);

  let actionsGroup: ActionComboBoxGroupItem[] = [
    {
      name: msg('changeHistory.resourcePicker.allActionsGroup'),
      id: 'actions',
      children: actionsItems,
    },
  ];

  if (resource === 'flag') {
    actionsGroup = [
      {
        name: msg('changeHistory.resourcePicker.flagTargetingChangesGroup'),
        id: 'targetingChanges',
        children: actionsItems.filter((action) => actionsRelatedToTargetingChanges.includes(action.id)),
      },
      {
        name: msg('changeHistory.resourcePicker.flagSettingsChangesGroup'),
        id: 'flagSettings',
        children: actionsItems.filter((action) => actionsRelatedToFlagSettings.includes(action.id)),
      },
      {
        name: msg('changeHistory.resourcePicker.environmentSettingsChangesGroup'),
        id: 'environmentSettings',
        children: actionsItems.filter((action) => actionsRelatedToEnvironmentSettings.includes(action.id)),
      },
      {
        name: msg('changeHistory.resourcePicker.collaborationChangesGroup'),
        id: 'collaboration',
        children: actionsItems.filter((action) => actionsRelatedToCollaboration.includes(action.id)),
      },
      {
        name: msg('changeHistory.resourcePicker.otherActionsGroup'),
        id: 'otherActions',
        children: actionsItems.filter(
          (action) =>
            !actionsRelatedToTargetingChanges.includes(action.id) &&
            !actionsRelatedToFlagSettings.includes(action.id) &&
            !actionsRelatedToEnvironmentSettings.includes(action.id) &&
            !actionsRelatedToCollaboration.includes(action.id),
        ),
      },
    ];
  }

  const handleFilterTextChange = (value: string) => {
    setFilterText(value);
  };

  const unfilteredActionsGroup = actionsGroup;

  // client side filter action items since the API does not support filtering
  if (filterText !== undefined && filterText !== '') {
    const filteredActions = actionsGroup.flatMap((group) => {
      const filteredChildren = group.children.filter(
        (item) =>
          item.id.toLowerCase().includes(filterText.toLowerCase()) ||
          item.description?.toLowerCase().includes(filterText.toLocaleLowerCase()),
      );

      return [{ ...group, children: filteredChildren }];
    });

    actionsGroup = filteredActions;
  }

  const checkboxGroupValue = getCheckboxGroupValue(actions ?? [], unfilteredActionsGroup);

  const listSelectedKeys: string[] | 'all' = actions?.includes(anyResource)
    ? 'all'
    : actions?.length
      ? Array.from(new Set(actions))
      : [];

  return (
    <CheckboxGroup
      value={checkboxGroupValue}
      className={styles.actionsDialog}
      onChange={(value) => {
        if (props?.onSelectionChange) {
          const isDeselectingAll = checkSubset(
            value,
            actionsGroup.map((group) => group.id),
          );
          if (isDeselectingAll) {
            // set the selected actions to the first group of actions
            props?.onSelectionChange(new Set(actionsGroup[0].children.map((b) => b.id)));
          } else {
            // Check if "Select all actions" is selected
            if (value.includes(anyResource)) {
              // When "Select all actions" is chosen, just return ["*"]
              props?.onSelectionChange(new Set([anyResource]));
            } else {
              const selectedActions = new Set(
                actionsGroup
                  .filter((a) => value.includes(a.id))
                  .flatMap((a) => a.children)
                  .map((b) => b.id),
              );
              props?.onSelectionChange(selectedActions);
            }
          }
        }
      }}
    >
      <ListBoxDialog<ActionComboBoxGroupItem>
        {...props}
        aria-label={msg('changeHistory.resourcePicker.actionsLabel')}
        items={actionsGroup}
        selectedKeys={listSelectedKeys}
        selectionMode="multiple"
        isBusy={false}
        onFilterChange={handleFilterTextChange}
        afterSearch={
          actionsGroup.length > 1 ? (
            <Box
              display="flex"
              color="$ui.tertiary"
              font="$label.2.medium"
              flexDirection="row-reverse"
              alignItems="center"
            >
              <LPCheckBox value={anyResource}>
                <Message id="changeHistory.resourcePicker.selectAllActions" />
              </LPCheckBox>
            </Box>
          ) : null
        }
      >
        {(section) => (
          <ListBoxSection id={section.name}>
            <Header>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                {section.name}
                <SelectAllCheckbox className={styles.checkbox} value={section.id}>
                  {({ isSelected }) => (
                    <div>
                      {isSelected
                        ? msg('changeHistory.resourcePicker.deselectAll')
                        : msg('changeHistory.resourcePicker.selectAll')}
                    </div>
                  )}
                </SelectAllCheckbox>
              </Box>
            </Header>
            <Collection items={section.children}>
              {(item) => (
                <ListBoxItem id={item.id} textValue={item.id}>
                  <Text slot="label">{item.id}</Text>
                  <Text slot="description">{item.description}</Text>
                </ListBoxItem>
              )}
            </Collection>
          </ListBoxSection>
        )}
      </ListBoxDialog>
    </CheckboxGroup>
  );
}

type MaintainerDialogProps = Omit<ListBoxDialogProps<MemberComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  maintainerId?: string;
};
function MemberDialog({ maintainerId, ...props }: MaintainerDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [getMembersQuery({ query: { limit: 5, sort: 'displayName', filter: { query: filterText } } })],
  });
  const [members] = data;

  // The coercion here is a bit weird: the queries won't run unless the values exist, but TypeScript can't infer that
  // from the enabled option on the queries.
  const maintainerMember = useMember({ memberId: maintainerId ?? '' }, { enabled: maintainerId !== undefined });

  const memberItems: MemberComboBoxItem[] = [];
  for (const member of members.data.items) {
    if (member._id === maintainerId && (filterText === undefined || filterText === '')) {
      continue;
    }

    const name = memberDisplayName(member);
    memberItems.push({
      id: member._id,
      name,
      email: member.email,
      initials: memberInitials(member),
      description: member.email !== name ? member.email : undefined,
    });
  }

  if (filterText === undefined || filterText === '') {
    if (maintainerMember.status === 'success') {
      const member = maintainerMember.data;
      const name = memberDisplayName(member);
      memberItems.unshift({
        id: maintainerId ?? '',
        name,
        email: member.email,
        initials: memberInitials(maintainerMember.data),
        description: member.email !== name ? member.email : undefined,
      });
    }
  }

  // add All option
  memberItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
    email: 'any@member.com',
    initials: 'a.a',
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<MemberComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.membersLabel')}
      items={memberItems.length ? memberItems : []}
      defaultSelectedKeys={maintainerId !== undefined ? [maintainerId] : []}
      selectionMode="single"
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem id={item.id} key={item.id} textValue={item.id}>
          <Text slot="label">
            <MemberAvatar size="small" email={item?.email} initials={item?.initials} />
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type TeamComboBoxItem = {
  id: string;
  name: string;
  description?: string;
};
type TeamDialogProps = Omit<ListBoxDialogProps<TeamComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  maintainerTeamKey?: string;
};
function TeamDialog({ maintainerTeamKey, ...props }: TeamDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');
  const data = useSuspenseQueries({
    queries: [teamsList({ params: { limit: 5, filter: { query: filterText } } })],
  });
  const [teams] = data;

  // The coercion here is a bit weird: the queries won't run unless the values exist, but TypeScript can't infer that
  // from the enabled option on the queries.
  const maintainerTeam = useTeam({ teamKey: maintainerTeamKey ?? '' }, { enabled: maintainerTeamKey !== undefined });

  const teamItems: TeamComboBoxItem[] = [];
  for (const team of nullthrows(teams.data.items)) {
    if (team.key === maintainerTeamKey && (filterText === undefined || filterText === '')) {
      continue;
    }

    teamItems.push({
      id: nullthrows(team.key),
      name: nullthrows(team.name),
    });
  }

  if (filterText === undefined || filterText === '') {
    if (maintainerTeam.status === 'success') {
      const team = maintainerTeam.data;
      teamItems.unshift({
        id: nullthrows(team.key),
        name: nullthrows(team.name),
      });
    }
  }

  // add any option
  teamItems.unshift({
    id: '*',
    name: msg('changeHistory.resourcePicker.allOption'),
  });

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<TeamComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.teamsLabel')}
      items={teamItems.length ? teamItems : []}
      selectionMode="single"
      defaultSelectedKeys={maintainerTeamKey !== undefined ? [maintainerTeamKey] : []}
      isBusy={isPending}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} textValue={item.id}>
          <Text slot="label">
            <Icon name="group" size="small" />
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
          {item.description && (
            <Text slot="description">
              <TextTruncator>{item.description}</TextTruncator>
            </Text>
          )}
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type AlertComboBoxItem = {
  id: string;
  name: string;
};

type AlertDialogProps = Omit<ListBoxDialogProps<AlertComboBoxItem>, 'isBusy' | 'onFilterChange' | 'children'> & {
  alertKey?: string;
  projectKey?: string;
  projectId?: string;
};

function AlertDialog({ alertKey, projectKey, projectId, ...props }: AlertDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const { data, loading } = useGetAlertOptionsQuery({
    variables: {
      project_id: projectId,
      query: filterText,
      limit: 10,
    },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  });

  const alertItems = useMemo(() => {
    const items =
      data?.alert_options?.map((alert) => ({
        id: alert.id,
        name: alert.name,
      })) ?? [];

    items.unshift({
      id: '*',
      name: msg('changeHistory.resourcePicker.allOption'),
    });

    return items;
  }, [data]);

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<AlertComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.alertsLabel')}
      items={alertItems}
      selectionMode="single"
      defaultSelectedKeys={alertKey !== undefined ? [alertKey] : ['*']}
      isBusy={isPending || loading}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}

type ObservabilityDashboardComboBoxItem = {
  id: string;
  name: string;
};

type ObservabilityDashboardDialogProps = Omit<
  ListBoxDialogProps<ObservabilityDashboardComboBoxItem>,
  'isBusy' | 'onFilterChange' | 'children'
> & {
  dashboardKey?: string;
  projectId?: string;
};

function ObservabilityDashboardDialog({ dashboardKey, projectId, ...props }: ObservabilityDashboardDialogProps) {
  const [isPending, startTransition] = useTransition();
  const [filterText, setFilterText] = useState('');

  const { data, loading } = useGetDashboardOptionsQuery({
    variables: {
      project_id: projectId,
      query: filterText,
      limit: 10,
    },
    fetchPolicy: 'cache-and-network',
    skip: !projectId,
  });

  const dashboardItems = useMemo(() => {
    const items =
      data?.dashboard_options?.map((dashboard) => ({
        id: dashboard.id,
        name: dashboard.name,
      })) ?? [];

    items.unshift({
      id: '*',
      name: msg('changeHistory.resourcePicker.allOption'),
    });

    return items;
  }, [data]);

  const handleFilterTextChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  return (
    <ListBoxDialog<ObservabilityDashboardComboBoxItem>
      {...props}
      aria-label={msg('changeHistory.resourcePicker.dashboardsLabel')}
      items={dashboardItems}
      selectionMode="single"
      defaultSelectedKeys={dashboardKey !== undefined ? [dashboardKey] : ['*']}
      isBusy={isPending || loading}
      onFilterChange={handleFilterTextChange}
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} textValue={item.id}>
          <Text slot="label">
            <TextTruncator>{item.name}</TextTruncator>
          </Text>
        </ListBoxItem>
      )}
    </ListBoxDialog>
  );
}
